import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle } from "lucide-react";
import QuoteDocument from "@/components/admin/QuoteDocument";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicQuotePage() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/public/quotes/${token}`)
      .then(({ data }) => setQuote(data.quote))
      .catch((err) => setError(err.response?.data?.detail || "This quote could not be found."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/quotes/${token}/${confirmAction}`);
      setConfirmAction(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
      setConfirmAction(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10" data-testid="public-quote-page">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 flex justify-center">
          <img src="/nova-logo-wide.png" alt="Nova Opsis" className="h-8 w-auto object-contain" />
        </div>
        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm font-semibold text-slate-500" data-testid="public-quote-loading">
            Loading your quote…
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-10 text-center" data-testid="public-quote-error">
            <AlertTriangle className="mx-auto mb-3 text-red-500" size={28} />
            <p className="text-sm font-semibold text-red-700">{error}</p>
          </div>
        ) : (
          <QuoteDocument
            quote={quote}
            mode="customer"
            onAccept={() => setConfirmAction("accept")}
            onDecline={() => setConfirmAction("decline")}
            busy={busy}
          />
        )}
      </div>

      {confirmAction && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 p-4" data-testid="public-quote-confirm-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-base font-bold text-slate-950">
              {confirmAction === "accept" ? "Accept this quote?" : "Decline this quote?"}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {confirmAction === "accept"
                ? "By accepting, you agree to the pricing, terms, and conditions outlined above."
                : "This quote will be marked as declined. This cannot be undone."}
            </p>
            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={confirm}
                disabled={busy}
                data-testid="public-quote-confirm-btn"
                className={`rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors duration-200 disabled:opacity-50 ${
                  confirmAction === "accept" ? "bg-[#0052FF] hover:bg-blue-700" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {confirmAction === "accept" ? "Yes, accept quote" : "Yes, decline quote"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmAction(null)}
                disabled={busy}
                data-testid="public-quote-cancel-btn"
                className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Keep reviewing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
